import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Play,
  Pause,
  RotateCcw,
  RotateCw,
  MapPin,
  X,
  ChevronDown,
  Share2,
  Heart,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { useFavorites } from '@/hooks/useFavorites';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface PodcastTrack {
  Name: string;
  City: string;
  Country: string;
  Image: string | null;
  AudioFile_FR: string | null;
  AudioFile_EN: string | null;
}

interface PodcastPlayerProps {
  podcast: PodcastTrack | null;
  language: 'FR' | 'EN';
  onLanguageChange?: (lang: 'FR' | 'EN') => void;
  /** Optional dismiss callback. When undefined, no close (X) button is shown. */
  onClose?: () => void;
  uiLanguage?: 'FR' | 'EN';
}

export interface PodcastPlayerHandle {
  /**
   * Starts playing AND opens the expanded view. Must be called synchronously
   * from a user-gesture event handler so autoplay is not blocked.
   */
  playAndExpand: () => void;
}

const PLAYBACK_SPEEDS = [1, 1.25, 1.5, 1.75, 0.75];

export const PodcastPlayer = forwardRef<PodcastPlayerHandle, PodcastPlayerProps>(({
  podcast,
  language,
  onLanguageChange,
  onClose,
  uiLanguage = 'FR',
}, ref) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  // Whether the user has interacted with the player at least once. Used to
  // show a one-time "tap to listen" nudge above the mini bar.
  const [hasInteracted, setHasInteracted] = useState(false);
  const [hintDismissed, setHintDismissed] = useState(false);

  const { isFavorite, toggleFavorite } = useFavorites();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const isFR = uiLanguage === 'FR';

  const audioFile = podcast
    ? (() => {
        const preferred = language === 'FR' ? podcast.AudioFile_FR : podcast.AudioFile_EN;
        const fallback = language === 'FR' ? podcast.AudioFile_EN : podcast.AudioFile_FR;
        return preferred || fallback;
      })()
    : null;

  // Audio source / event listeners
  useEffect(() => {
    if (!podcast || !audioFile || !audioRef.current) return;

    const audio = audioRef.current;
    audio.src = audioFile;
    audio.load();

    setIsPlaying(false);
    setCurrentTime(0);

    const handleLoadedMetadata = () => {
      setDuration(audio.duration || 0);
      setCurrentTime(0);
    };
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [podcast, audioFile]);

  // The bar starts paused. Audio plays when the user clicks play in the bar
  // or in the expanded view. We avoid autoplay because (a) browsers block it
  // and (b) the user may simply be browsing the page.

  // Apply playback rate
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate, audioFile]);

  // Imperative API exposed to the parent (e.g. hero "Listen" pill).
  // Must be called synchronously from a user gesture so autoplay is allowed.
  useImperativeHandle(
    ref,
    () => ({
      playAndExpand: () => {
        setIsExpanded(true);
        setHasInteracted(true);
        const audio = audioRef.current;
        if (!audio) return;
        // Try to play immediately within the user-gesture event chain.
        const playPromise = audio.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            // If the audio isn't ready yet, retry as soon as it can play.
            const retry = () => {
              audio.play().catch(() => undefined);
            };
            audio.addEventListener('canplay', retry, { once: true });
          });
        }
      },
    }),
    []
  );

  // Auto-dismiss the "tap to listen" hint after a generous delay so it never
  // lingers if the user is reading the page.
  useEffect(() => {
    if (hasInteracted || hintDismissed) return;
    const id = window.setTimeout(() => setHintDismissed(true), 12000);
    return () => window.clearTimeout(id);
  }, [hasInteracted, hintDismissed]);

  const togglePlayPause = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (!audioRef.current) return;
      setHasInteracted(true);
      if (isPlaying) audioRef.current.pause();
      else audioRef.current.play().catch(() => undefined);
    },
    [isPlaying]
  );

  const handleSeek = (value: number[]) => {
    if (!audioRef.current) return;
    const newTime = value[0];
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const skipBackward = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.max(0, currentTime - 15);
  };

  const skipForward = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.min(duration, currentTime + 15);
  };

  const cyclePlaybackSpeed = () => {
    const idx = PLAYBACK_SPEEDS.indexOf(playbackRate);
    const next = PLAYBACK_SPEEDS[(idx + 1) % PLAYBACK_SPEEDS.length];
    setPlaybackRate(next);
  };

  const handleVolumeChange = (value: number[]) => {
    const v = value[0];
    setVolume(v);
    if (audioRef.current) audioRef.current.volume = v;
    setIsMuted(v === 0);
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    if (isMuted) {
      audioRef.current.volume = volume || 1;
      setIsMuted(false);
    } else {
      audioRef.current.volume = 0;
      setIsMuted(true);
    }
  };

  const handleShare = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!podcast) return;
    const shareData = {
      title: podcast.Name,
      text: isFR ? `Écoutez ce podcast : ${podcast.Name}` : `Listen to this podcast: ${podcast.Name}`,
      url: window.location.href,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast({
          title: isFR ? 'Lien copié' : 'Link copied',
          description: isFR
            ? 'Le lien du podcast a été copié dans le presse-papiers.'
            : 'The podcast link was copied to your clipboard.',
        });
      }
    } catch {
      /* user cancelled share */
    }
  };

  const handleToggleFavorite = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!podcast) return;
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    toggleFavorite(podcast.Name);
  };

  const formatTime = (time: number) => {
    if (!Number.isFinite(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (!podcast) return null;

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const placeFav = isFavorite(podcast.Name);
  const showHint = !hasInteracted && !hintDismissed;

  return (
    <>
      {/* Mini bar - always visible at bottom-center */}
      <div
        className="fixed left-2 right-2 md:left-1/2 md:right-auto md:-translate-x-1/2 md:w-full md:max-w-2xl z-[60]"
        style={{ bottom: 'calc(var(--mobile-nav-height, 16px) + 8px)' }}
      >
        {/* First-time nudge: subtle tooltip above the bar */}
        {showHint && (
          <div className="mb-2 flex justify-center pointer-events-none animate-[fade-in_0.4s_ease-out]">
            <div className="pointer-events-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-foreground text-background text-xs font-semibold shadow-xl">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              {isFR ? 'Touchez pour écouter' : 'Tap to listen'}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setHintDismissed(true);
                }}
                className="ml-1 -mr-1 w-4 h-4 inline-flex items-center justify-center rounded-full hover:bg-background/20"
                aria-label={isFR ? 'Ignorer' : 'Dismiss'}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}
        <div
          role="button"
          tabIndex={0}
          onClick={() => setIsExpanded(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsExpanded(true);
            }
          }}
          aria-label={isFR ? 'Ouvrir le lecteur audio' : 'Open audio player'}
          className="group relative cursor-pointer rounded-2xl bg-card/95 backdrop-blur-md border border-border/60 shadow-2xl overflow-hidden hover:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.35)] transition-shadow"
        >
          {/* Top progress bar */}
          <div className="absolute inset-x-0 top-0 h-[3px] bg-muted/40 overflow-hidden">
            <div
              className="h-full bg-primary transition-[width] duration-200 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="flex items-center gap-3 p-2.5 pl-3">
            {/* Cover */}
            <div className="relative w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 shadow-md bg-muted">
              {podcast.Image ? (
                <img
                  src={podcast.Image}
                  alt={podcast.Name}
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              ) : (
                <div className="w-full h-full bg-gradient-earth flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-white/80" />
                </div>
              )}
              {/* Equalizer pulse when playing */}
              {isPlaying && (
                <div className="absolute inset-0 bg-black/20 flex items-end justify-center gap-0.5 pb-1.5 pointer-events-none">
                  <span className="block w-0.5 bg-white/90 rounded-full animate-[pulse_1s_ease-in-out_infinite] h-2" />
                  <span className="block w-0.5 bg-white/90 rounded-full animate-[pulse_1s_ease-in-out_infinite] h-3 [animation-delay:120ms]" />
                  <span className="block w-0.5 bg-white/90 rounded-full animate-[pulse_1s_ease-in-out_infinite] h-1.5 [animation-delay:240ms]" />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm leading-tight truncate text-foreground">
                {podcast.Name}
              </p>
              <p className="text-xs text-muted-foreground truncate flex items-center gap-1 mt-0.5">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">
                  {podcast.City}
                  {podcast.Country ? `, ${podcast.Country}` : ''}
                </span>
              </p>
            </div>

            {/* Mini controls */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <Button
                onClick={togglePlayPause}
                size="icon"
                className="w-11 h-11 rounded-full btn-adventure shadow-md hover:scale-105 active:scale-95 transition-transform"
                aria-label={
                  isPlaying ? (isFR ? 'Mettre en pause' : 'Pause') : isFR ? 'Lecture' : 'Play'
                }
              >
                {isPlaying ? (
                  <Pause className="w-5 h-5" />
                ) : (
                  <Play className="w-5 h-5 ml-0.5" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded full-screen player */}
      <Sheet open={isExpanded} onOpenChange={setIsExpanded}>
        <SheetContent
          side="bottom"
          className="h-[100dvh] w-full max-w-none p-0 border-0 bg-gradient-to-b from-primary/10 via-background to-background overflow-y-auto [&>button.absolute]:hidden"
        >
          {/* Accessible labels (visually hidden) */}
          <SheetTitle className="sr-only">{podcast.Name}</SheetTitle>
          <SheetDescription className="sr-only">
            {isFR
              ? `Lecteur audio pour ${podcast.Name}, ${podcast.City}`
              : `Audio player for ${podcast.Name}, ${podcast.City}`}
          </SheetDescription>
          <div className="relative flex flex-col h-full px-6 pt-4 pb-8 max-w-xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsExpanded(false)}
                className="w-10 h-10 rounded-full"
                aria-label={isFR ? 'Réduire le lecteur' : 'Minimize player'}
              >
                <ChevronDown className="w-6 h-6" />
              </Button>
              <div className="text-center">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  {isFR ? 'En écoute' : 'Now listening'}
                </p>
                <p className="text-xs font-medium text-foreground/80 truncate max-w-[180px]">
                  {podcast.City}
                </p>
              </div>
              {onClose ? (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    if (audioRef.current) audioRef.current.pause();
                    setIsExpanded(false);
                    onClose();
                  }}
                  className="w-10 h-10 rounded-full text-muted-foreground hover:text-foreground"
                  aria-label={isFR ? 'Fermer' : 'Close'}
                >
                  <X className="w-5 h-5" />
                </Button>
              ) : (
                <span className="w-10 h-10" aria-hidden="true" />
              )}
            </div>

            {/* Cover - square, centered */}
            <div className="flex-1 flex items-center justify-center my-4 min-h-0">
              <div className="relative w-full aspect-square max-w-sm mx-auto rounded-2xl overflow-hidden shadow-2xl">
                {podcast.Image ? (
                  <img
                    src={podcast.Image}
                    alt={podcast.Name}
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-earth flex items-center justify-center">
                    <MapPin className="w-20 h-20 text-white/80" />
                  </div>
                )}
              </div>
            </div>

            {/* Title + city + favorite */}
            <div className="flex items-start justify-between gap-3 mb-5">
              <div className="min-w-0 flex-1">
                <h2 className="text-xl sm:text-2xl font-bold leading-tight truncate">
                  {podcast.Name}
                </h2>
                <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">
                    {podcast.City}
                    {podcast.Country ? `, ${podcast.Country}` : ''}
                  </span>
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleToggleFavorite}
                className={cn(
                  'w-10 h-10 rounded-full flex-shrink-0',
                  placeFav && 'text-red-500 hover:text-red-600'
                )}
                aria-label={
                  placeFav
                    ? isFR
                      ? 'Retirer des favoris'
                      : 'Remove from favorites'
                    : isFR
                      ? 'Ajouter aux favoris'
                      : 'Add to favorites'
                }
              >
                <Heart className={cn('w-5 h-5', placeFav && 'fill-current')} />
              </Button>
            </div>

            {/* Progress */}
            <div className="mb-6">
              <Slider
                value={[currentTime]}
                max={duration || 100}
                step={1}
                onValueChange={handleSeek}
                className="w-full [&_[role=slider]]:w-3.5 [&_[role=slider]]:h-3.5"
                aria-label={isFR ? 'Position de lecture' : 'Playback position'}
              />
              <div className="flex justify-between mt-1.5 text-[11px] tabular-nums text-muted-foreground font-medium">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Main controls */}
            <div className="flex items-center justify-center gap-2 sm:gap-4 mb-6">
              <Button
                variant="ghost"
                size="icon"
                onClick={cyclePlaybackSpeed}
                className="w-12 h-12 rounded-full text-muted-foreground hover:text-foreground"
                aria-label={`${isFR ? 'Vitesse' : 'Speed'}: ${playbackRate}x`}
              >
                <span className="text-xs font-bold tabular-nums">
                  {playbackRate === 0.75 ? '0.75×' : `${playbackRate}×`}
                </span>
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={skipBackward}
                className="relative w-12 h-12 rounded-full hover:bg-muted"
                aria-label={isFR ? 'Reculer de 15 secondes' : 'Rewind 15 seconds'}
              >
                <RotateCcw className="w-6 h-6" />
                <span className="absolute -bottom-0.5 -right-0.5 text-[9px] font-bold text-primary bg-background rounded-full w-4 h-4 flex items-center justify-center border border-primary shadow-sm">
                  15
                </span>
              </Button>

              <Button
                onClick={togglePlayPause}
                className="w-16 h-16 rounded-full btn-adventure shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transition-all"
                aria-label={
                  isPlaying ? (isFR ? 'Mettre en pause' : 'Pause') : isFR ? 'Lecture' : 'Play'
                }
              >
                {isPlaying ? (
                  <Pause className="w-7 h-7" />
                ) : (
                  <Play className="w-7 h-7 ml-1" />
                )}
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={skipForward}
                className="relative w-12 h-12 rounded-full hover:bg-muted"
                aria-label={isFR ? 'Avancer de 15 secondes' : 'Forward 15 seconds'}
              >
                <RotateCw className="w-6 h-6" />
                <span className="absolute -bottom-0.5 -right-0.5 text-[9px] font-bold text-primary bg-background rounded-full w-4 h-4 flex items-center justify-center border border-primary shadow-sm">
                  15
                </span>
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={handleShare}
                className="w-12 h-12 rounded-full text-muted-foreground hover:text-foreground"
                aria-label={isFR ? 'Partager' : 'Share'}
              >
                <Share2 className="w-5 h-5" />
              </Button>
            </div>

            {/* Bottom row: language + volume */}
            <div className="flex items-center justify-between gap-3">
              {/* Language switch */}
              {podcast.AudioFile_FR && podcast.AudioFile_EN && onLanguageChange ? (
                <div className="inline-flex rounded-full bg-muted p-0.5 text-xs font-semibold">
                  <button
                    type="button"
                    onClick={() => onLanguageChange('FR')}
                    className={cn(
                      'px-3 py-1.5 rounded-full transition-colors',
                      language === 'FR'
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    FR
                  </button>
                  <button
                    type="button"
                    onClick={() => onLanguageChange('EN')}
                    className={cn(
                      'px-3 py-1.5 rounded-full transition-colors',
                      language === 'EN'
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    EN
                  </button>
                </div>
              ) : (
                <span className="text-xs font-medium text-muted-foreground px-2">
                  {language === 'FR' ? 'Français' : 'English'}
                </span>
              )}

              {/* Volume - hidden on small mobile */}
              <div className="hidden sm:flex items-center gap-2 flex-1 max-w-[180px] ml-auto">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleMute}
                  className="w-8 h-8 rounded-full text-muted-foreground"
                  aria-label={isMuted ? (isFR ? 'Activer le son' : 'Unmute') : isFR ? 'Couper le son' : 'Mute'}
                >
                  {isMuted || volume === 0 ? (
                    <VolumeX className="w-4 h-4" />
                  ) : (
                    <Volume2 className="w-4 h-4" />
                  )}
                </Button>
                <Slider
                  value={[isMuted ? 0 : volume]}
                  max={1}
                  step={0.05}
                  onValueChange={handleVolumeChange}
                  className="flex-1 [&_[role=slider]]:w-3 [&_[role=slider]]:h-3"
                  aria-label={isFR ? 'Volume' : 'Volume'}
                />
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Hidden audio element */}
      <audio ref={audioRef} preload="metadata" />
    </>
  );
});

PodcastPlayer.displayName = 'PodcastPlayer';

export default PodcastPlayer;
